import { View, Text, StyleSheet, Font, Link } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import MarkdownRenderer from "../custom-markdown";
import { AiAssessmentScaleCardProps } from "./AiAssessmentScaleCard";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
import PdfLayout from "../pdf/PdfLayout";

Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

// Helper to get level colors for PDF
const getLevelPdfStyle = (level: number) => {
  switch (level) {
    case 1: // NO AI
      return {
        backgroundColor: "#bcf0fa",
        color: "#1e40af",
        borderColor: "#3b82f6",
        titleColor: "#1e40af",
      };
    case 2: // AI PLANNING
      return {
        backgroundColor: "#c1f5dc",
        color: "#166534",
        borderColor: "#22c55e",
        titleColor: "#166534",
      };
    case 3: // AI COLLABORATION
      return {
        backgroundColor: "#d6e5ff",
        color: "#312e81",
        borderColor: "#6366f1",
        titleColor: "#312e81",
      };
    case 4: // FULL AI
      return {
        backgroundColor: "#fbd38d",
        color: "#9a3412",
        borderColor: "#f97316",
        titleColor: "#9a3412",
      };
    case 5: // AI EXPLORATION
      return {
        backgroundColor: "#f5cfff",
        color: "#831843",
        borderColor: "#d946ef",
        titleColor: "#831843",
      };
    default:
      return {
        backgroundColor: "#ccfbf1",
        color: "#0f766e",
        borderColor: "#14b8a6",
        titleColor: "#0f766e",
      };
  }
};

// Helper to get level name translation key based on level number
const getLevelNameKey = (levelNumber: number) => {
  switch (levelNumber) {
    case 1:
      return "noAi"; // NO AI
    case 2:
      return "aiPlanning"; // AI PLANNING
    case 3:
      return "aiCollaboration"; // AI COLLABORATION
    case 4:
      return "fullAi"; // FULL AI
    case 5:
      return "aiExploration"; // AI EXPLORATION
    default:
      return "";
  }
};

const styles = StyleSheet.create({
  page: {
    padding: 20,
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 5,
  },
  logo: {
    width: 100,
    height: 30,
    objectFit: "contain",
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 8,
    textAlign: "center",
    color: "#0f766e",
    fontFamily: "Montserrat",
  },
  headerInfo: {
    fontSize: 9,
    marginBottom: 6,
    textAlign: "center",
    fontFamily: "Montserrat",
    color: "#374151",
  },
  table: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginTop: 4,
    flexGrow: 1,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#0d9488",
    borderBottomWidth: 1,
    borderBottomColor: "#475569",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#475569",
  },
  levelCell: {
    width: "5%",
    padding: 5,
    borderRightWidth: 1,
    borderRightColor: "#475569",
    alignItems: "center",
    justifyContent: "center",
  },
  nameCell: {
    width: "11%",
    padding: 5,
    borderRightWidth: 1,
    borderRightColor: "#475569",
    justifyContent: "center",
  },
  descriptionCell: {
    width: "42%",
    padding: 5,
    borderRightWidth: 1,
    borderRightColor: "#475569",
  },
  examplesCell: {
    width: "42%",
    padding: 5,
  },
  headerText: {
    color: "white",
    fontSize: 8,
    fontWeight: "bold",
    textAlign: "center",
    fontFamily: "Montserrat",
  },
  levelNumberContainer: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: "#0d9488",
    alignItems: "center",
    justifyContent: "center",
  },
  levelNumber: {
    color: "white",
    fontSize: 8,
    fontWeight: "bold",
    fontFamily: "Montserrat",
  },
  levelName: {
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "center",
    fontFamily: "Montserrat",
  },
  contentText: {
    fontSize: 7,
    fontFamily: "Montserrat",
    color: "#374151",
  },
  strongText: {
    fontWeight: "bold",
    color: "#1f2937",
    fontSize: 7,
    fontFamily: "Montserrat",
  },
  listItem: {
    flexDirection: "row",
    marginBottom: 2,
    paddingRight: 12,
  },
  bulletPoint: {
    width: 8,
    fontSize: 7,
    fontFamily: "Montserrat",
  },
  footer: {
    marginTop: 15,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    alignItems: "center",
  },
  footerTitle: {
    fontSize: 10,
    fontWeight: "bold",
    fontFamily: "Montserrat",
    textAlign: "center",
    marginBottom: 4,
    color: "#0f766e",
    textDecoration: "none",
  },
  footerText: {
    fontSize: 8,
    fontFamily: "Montserrat",
    textAlign: "center",
    color: "#4b5563",
  },
  footerLink: {
    color: "#0d9488",
    textDecoration: "none",
  },
});

const AiAssessmentScaleDocument = ({
  assessmentScale,
  customizationSettings,
}: AiAssessmentScaleCardProps) => {
  const { t } = useTranslation();

  // Sort levels by level_number if levels exist
  const sortedLevels = assessmentScale.levels
    ? [...assessmentScale.levels].sort(
        (a, b) => (a.level_number || 0) - (b.level_number || 0),
      )
    : [];
  const headerStyle = [
    styles.headerText,
    {
      fontSize: scaleFontSize(
        styles.headerText.fontSize,
        customizationSettings?.fontSize,
      ),
    },
  ];
  return (
    <PdfLayout
      courseName={assessmentScale.courseName}
      gradeLevel={assessmentScale.gradeLevel}
      topic={assessmentScale.title || assessmentScale.tableTitle || ""}
      imageUrl={assessmentScale.imageUrl}
      headerTextColor={"#0d9488"}
      customizationSettings={customizationSettings}
      createdDate={assessmentScale.createdAt}
      author={assessmentScale.authorName}
      orientation="landscape"
    >
      {/* {assessmentScale.courseName && (
          <Text style={styles.headerInfo}>
            {t("common.course")}: {assessmentScale.courseName}
            {assessmentScale.gradeLevel
              ? ` • ${t("common.grade")}: ${assessmentScale.gradeLevel}`
              : ""}
          </Text>
        )} */}

      <View style={styles.table}>
        {/* Table Header */}
        <View style={styles.tableHeader}>
          <View style={styles.levelCell}>
            <Text style={headerStyle}>{t("aiAssessmentScale.level")}</Text>
          </View>
          <View style={styles.nameCell}>
            <Text style={headerStyle}>{t("aiAssessmentScale.levelName")}</Text>
          </View>
          <View style={styles.descriptionCell}>
            <Text style={headerStyle}>
              {t("aiAssessmentScale.descriptionTable")}
            </Text>
          </View>
          <View style={styles.examplesCell}>
            <Text style={headerStyle}>{t("aiAssessmentScale.examples")}</Text>
          </View>
        </View>

        {/* Table Rows */}
        {sortedLevels.map((level) => {
          const levelNumber = level.level_number || 0;
          const levelStyle = getLevelPdfStyle(levelNumber);

          return (
            <View
              key={`row-${levelNumber}`}
              style={[
                styles.tableRow,
                { backgroundColor: levelStyle.backgroundColor },
              ]}
            >
              <View style={styles.levelCell}>
                <View
                  style={[
                    styles.levelNumberContainer,
                    { backgroundColor: levelStyle.borderColor },
                  ]}
                >
                  <Text
                    style={[
                      styles.levelNumber,
                      {
                        fontSize: scaleFontSize(
                          styles.levelNumber.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    {levelNumber}
                  </Text>
                </View>
              </View>

              <View style={styles.nameCell}>
                <Text
                  style={[
                    styles.levelName,
                    {
                      color: levelStyle.titleColor,
                      fontSize: scaleFontSize(
                        styles.levelName.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {levelNumber > 0
                    ? t(
                        `aiAssessmentScale.levelNames.${getLevelNameKey(levelNumber)}`,
                      )
                    : level.level_name || ""}
                </Text>
              </View>

              <View style={styles.descriptionCell}>
                {level.description && (
                  <MarkdownRenderer
                    customStyle={{
                      text: {
                        text: {
                          ...styles.contentText,
                          fontSize: scaleFontSize(
                            styles.contentText.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                      },
                      listItem: {
                        text: {
                          ...styles.contentText,
                          fontSize: scaleFontSize(
                            styles.contentText.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                      },
                    }}
                    content={level.description}
                  />
                )}
              </View>

              <View style={styles.examplesCell}>
                {level.examples && level.examples.length > 0 && (
                  <View>
                    {level.examples.map((example, idx) => (
                      <View key={idx} style={styles.listItem}>
                        <Text style={styles.bulletPoint}>• </Text>
                        <MarkdownRenderer
                          customStyle={{
                            text: {
                              text: {
                                ...styles.contentText,
                                fontSize: scaleFontSize(
                                  styles.contentText.fontSize,
                                  customizationSettings?.fontSize,
                                ),
                              },
                            },
                            listItem: {
                              text: {
                                ...styles.contentText,
                                fontSize: scaleFontSize(
                                  styles.contentText.fontSize,
                                  customizationSettings?.fontSize,
                                ),
                              },
                            },
                          }}
                          content={example}
                        />
                      </View>
                    ))}
                  </View>
                )}
              </View>
            </View>
          );
        })}
      </View>

      {/* Attribution Footer */}
      <View style={styles.footer}>
        <Link src="https://leonfurze.com/2024/08/28/updating-the-ai-assessment-scale/">
          <Text
            style={[
              styles.footerTitle,
              {
                fontSize: scaleFontSize(
                  styles.footerTitle.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
            ]}
          >
            AI Assessment Scale (AIAS) 2024 by Perkins, Furze, Roe & MacVaugh
          </Text>
        </Link>
        <Link src="https://leonfurze.com/2024/08/28/updating-the-ai-assessment-scale/">
          <Text
            style={[
              styles.footerText,
              {
                fontSize: scaleFontSize(
                  styles.footerText.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
            ]}
          ></Text>
        </Link>
      </View>
    </PdfLayout>
  );
};

function AiAssessmentScaleCardPdf(props: AiAssessmentScaleCardProps) {
  return <AiAssessmentScaleDocument {...props} />;
}

export default AiAssessmentScaleCardPdf;
